import React, { useState, useEffect } from "react";
import { Button } from "@heroui/button";
import { Input } from "@heroui/input";

import { menuService } from "../../services/menuService";
import { Addon, AddonKind, getAddonKindName } from "../../types/menu";

interface AddonsManagerProps {
  productId: number;
  productTypeId?: number;
  readOnly?: boolean;
  onAddonsChange?: () => void;
}

const AddonsManager: React.FC<AddonsManagerProps> = ({
  productId,
  productTypeId,
  readOnly = false,
  onAddonsChange,
}) => {
  const [addons, setAddons] = useState<Addon[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAddAddonForm, setShowAddAddonForm] = useState(false);
  const [showAddSelectionForm, setShowAddSelectionForm] = useState<
    number | null
  >(null);
  const [expandedAddonId, setExpandedAddonId] = useState<number | null>(null);

  // Form states
  const [newAddonName, setNewAddonName] = useState("");
  const [newAddonKind, setNewAddonKind] = useState<number>(AddonKind.Optional);
  const [newSelectionName, setNewSelectionName] = useState("");
  const [newSelectionPrice, setNewSelectionPrice] = useState("");

  // Fetch addons
  useEffect(() => {
    const fetchAddons = async () => {
      try {
        setLoading(true);
        setError(null);

        let addonsList = [];

        if (productTypeId) {
          // Este es el caso que debería funcionar ahora que arreglamos el backend
          const response =
            await menuService.getProductTypeAddons(productTypeId);

          addonsList = response || [];
        } else if (productId) {
          const product = await menuService.getProductById(productId);

          addonsList = product?.addons || [];
        } else {
          addonsList = await menuService.getAddons();
        }

        // Asegúrate de que cada addon tenga un array de selections
        const processedAddons = addonsList.map((addon) => ({
          ...addon,
          selections: addon.selections || [],
        }));

        setAddons(processedAddons);

        if (processedAddons.length > 0) {
          setExpandedAddonId(processedAddons[0].id);
        }
      } catch (err) {
        console.error("Failed to fetch addons:", err);
        setError("Failed to load addons. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    menuService;

    fetchAddons();
  }, [productId, productTypeId]);

  // Create a new addon
  const handleCreateAddon = async () => {
    if (!newAddonName.trim()) {
      setError("Addon name is required");

      return;
    }

    try {
      setLoading(true);
      setError(null);

      const addon = await menuService.createAddon({
        name: newAddonName.trim(),
        kind: newAddonKind,
      });

      // If a product type is provided, associate the addon with it
      if (productTypeId) {
        await menuService.addAddonToProductType(productTypeId, addon.id);
      }

      // Update the addons list
      setAddons((prev) => [...prev, { ...addon, selections: [] }]);
      setNewAddonName("");
      setShowAddAddonForm(false);

      if (onAddonsChange) {
        onAddonsChange();
      }
    } catch (err) {
      console.error("Failed to create addon:", err);
      setError("Failed to create addon. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Add a selection to an addon
  const handleAddSelection = async (addonId: number) => {
    if (!newSelectionName.trim()) {
      setError("Selection name is required");

      return;
    }

    const price = parseFloat(newSelectionPrice);

    if (isNaN(price) || price < 0) {
      setError("Price must be a valid number greater than or equal to 0");

      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Create the selection
      const selection = await menuService.createSelection({
        name: newSelectionName.trim(),
        addon_id: addonId,
      });

      // Create the selection price
      // For simplicity, we're using kind=1 for all selections
      // In a real app, you might have different kinds for different product types/sizes
      await menuService.createSelectionPrice({
        selection_id: selection.id,
        price: Math.round(price * 100), // Convert to cents
        kind: 1,
      });

      // Update the addons list
      setAddons((prev) =>
        prev.map((addon) => {
          if (addon.id === addonId) {
            return {
              ...addon,
              selections: [
                ...addon.selections,
                {
                  ...selection,
                  prices: [
                    {
                      id: 0,
                      selection_id: selection.id,
                      price: Math.round(price * 100),
                      kind: 1,
                    },
                  ],
                },
              ],
            };
          }

          return addon;
        }),
      );

      setNewSelectionName("");
      setNewSelectionPrice("");
      setShowAddSelectionForm(null);

      if (onAddonsChange) {
        onAddonsChange();
      }
    } catch (err) {
      console.error("Failed to add selection:", err);
      setError("Failed to add selection. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Delete an addon
  const handleDeleteAddon = async (addonId: number) => {
    if (
      window.confirm(
        "Are you sure you want to delete this addon? This will also delete all associated selections.",
      )
    ) {
      try {
        setLoading(true);
        setError(null);

        // If a product type is provided, remove the association first
        if (productTypeId) {
          await menuService.removeAddonFromProductType(productTypeId, addonId);
        }

        // Delete the addon
        await menuService.deleteAddon(addonId);

        // Update the addons list
        setAddons((prev) => prev.filter((addon) => addon.id !== addonId));

        if (onAddonsChange) {
          onAddonsChange();
        }
      } catch (err) {
        console.error("Failed to delete addon:", err);
        setError("Failed to delete addon. Please try again.");
      } finally {
        setLoading(false);
      }
    }
  };

  // Format price from cents to dollars
  const formatPrice = (cents: number): string => {
    const dollars = cents / 100;

    return `$${dollars.toFixed(2)}`;
  };

  // Loading state
  if (loading && addons.length === 0) {
    return (
      <div className="py-4 text-center">
        <div className="inline-block w-6 h-6 border-4 border-t-primary-500 border-r-primary-500/30 border-b-primary-500/30 border-l-primary-500/30 rounded-full animate-spin mr-2" />
        <span className="text-gray-500 dark:text-gray-400">
          Loading addons...
        </span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Error message */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/30 text-red-800 dark:text-red-200 p-4 rounded-lg">
          {error}
        </div>
      )}

      {/* Add addon button */}
      {!readOnly && (
        <div className="flex justify-end">
          {!showAddAddonForm ? (
            <Button
              color="primary"
              variant="light"
              onPress={() => setShowAddAddonForm(true)}
            >
              Add New Addon
            </Button>
          ) : (
            <div className="w-full bg-gray-50 dark:bg-gray-800 p-4 rounded-lg space-y-3">
              <h3 className="text-sm font-medium">Add New Addon</h3>
              <div className="flex flex-col sm:flex-row gap-3">
                <Input
                  className="flex-1"
                  placeholder="Addon name"
                  size="sm"
                  value={newAddonName}
                  onChange={(e) => setNewAddonName(e.target.value)}
                />
                <select
                  className="rounded-md border border-gray-300 dark:border-gray-600 px-3 py-2 text-sm dark:bg-gray-700 dark:text-white"
                  value={newAddonKind}
                  onChange={(e) => setNewAddonKind(parseInt(e.target.value))}
                >
                  <option value={AddonKind.Required}>Required</option>
                  <option value={AddonKind.Optional}>Optional</option>
                  <option value={AddonKind.Multiple}>Multiple Selection</option>
                </select>
                <div className="flex gap-2">
                  <Button
                    color="primary"
                    disabled={loading}
                    size="sm"
                    onPress={handleCreateAddon}
                  >
                    Save
                  </Button>
                  <Button
                    size="sm"
                    variant="light"
                    onPress={() => {
                      setShowAddAddonForm(false);
                      setNewAddonName("");
                    }}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Addons list */}
      {addons.length === 0 ? (
        <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-8 text-center">
          <p className="text-gray-500 dark:text-gray-400">
            No addons available.{" "}
            {!readOnly && "Add your first addon to get started."}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {addons.map((addon) => (
            <div
              key={addon.id}
              className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden"
            >
              {/* Addon header */}
              <div className="flex items-center justify-between bg-gray-50 dark:bg-gray-800 px-4 py-3">
                <div>
                  <h3 className="font-medium text-gray-900 dark:text-white">
                    {addon.name}
                  </h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {getAddonKindName(addon.kind)}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  {!readOnly && (
                    <Button
                      color="danger"
                      size="sm"
                      variant="light"
                      onPress={() => handleDeleteAddon(addon.id)}
                    >
                      Delete
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant="light"
                    onPress={() =>
                      setExpandedAddonId(
                        expandedAddonId === addon.id ? null : addon.id,
                      )
                    }
                  >
                    {expandedAddonId === addon.id ? "Collapse" : "Expand"}
                  </Button>
                </div>
              </div>

              {/* Expanded content - selections */}
              {expandedAddonId === addon.id && (
                <div className="p-4 border-t border-gray-200 dark:border-gray-700">
                  {/* Selections list */}
                  <div className="mb-4">
                    <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Selections/Options
                    </h4>
                    {addon.selections?.length === 0 ? (
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        No selections available for this addon.
                      </p>
                    ) : (
                      <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-md overflow-hidden">
                        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                          <thead className="bg-gray-50 dark:bg-gray-800">
                            <tr>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                Name
                              </th>
                              <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                Price
                              </th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                            {addon.selections.map((selection) => (
                              <tr key={selection.id}>
                                <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">
                                  {selection.name}
                                </td>
                                <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300 text-right">
                                  {selection.prices &&
                                  selection.prices.length > 0
                                    ? formatPrice(selection.prices[0].price)
                                    : "$0.00"}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>

                  {/* Add selection form */}
                  {!readOnly && (
                    <div className="mt-4">
                      {showAddSelectionForm === addon.id ? (
                        <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg space-y-3">
                          <h4 className="text-sm font-medium">
                            Add New Selection
                          </h4>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <Input
                              placeholder="Selection name"
                              size="sm"
                              value={newSelectionName}
                              onChange={(e) =>
                                setNewSelectionName(e.target.value)
                              }
                            />
                            <Input
                              min="0"
                              placeholder="Price (0.00)"
                              size="sm"
                              step="0.01"
                              type="number"
                              value={newSelectionPrice}
                              onChange={(e) =>
                                setNewSelectionPrice(e.target.value)
                              }
                            />
                          </div>
                          <div className="flex gap-2 justify-end">
                            <Button
                              color="primary"
                              disabled={loading}
                              size="sm"
                              onPress={() => handleAddSelection(addon.id)}
                            >
                              Add
                            </Button>
                            <Button
                              size="sm"
                              variant="light"
                              onPress={() => {
                                setShowAddSelectionForm(null);
                                setNewSelectionName("");
                                setNewSelectionPrice("");
                              }}
                            >
                              Cancel
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <Button
                          size="sm"
                          variant="light"
                          onPress={() => setShowAddSelectionForm(addon.id)}
                        >
                          Add Selection
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AddonsManager;
